import React, { useState, useEffect } from 'react'
import { Typography, Card, CardContent, Box, Chip, Avatar, CircularProgress } from '@mui/material'
import { Person, Cancel, Warning } from '@mui/icons-material'
import styled from 'styled-components'
import { dateFormatMonth } from '../../../services/functions'
import { getCancelComments, getRangeDateFromPeriod } from '../../../services/subscriptionCancel'
import { useMerchant } from '../../../store/global/useMerchant'

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #ff6b6b;
  transition: transform 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`

const CommentHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`

const CommentContent = styled(Typography)`
  margin-bottom: 12px !important;
  line-height: 1.6 !important;
  color: #333 !important;
`

const CommentTags = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`

const StyledChip = styled(Chip)`
  background-color: #ff6b6b !important;
  color: white !important;
  font-size: 0.75rem !important;
`

const EmptyState = styled(Box)`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
`

export function CommentsCancelamento({ period }) {
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(false)
  const merchant = useMerchant((st) => st.data.merchant)

  useEffect(() => {
    const fetchComments = async () => {
      
      setLoading(true)
      try {
        const rangeDate = getRangeDateFromPeriod(period)
        const commentsData = await getCancelComments(rangeDate)

        const total = commentsData.total || 0;
        
        // Transformar dados da API para o formato esperado pelo componente
        const formattedComments = commentsData?.comments?.map((comment, index) => ({
          date: comment.canceled_at || new Date().toISOString(),
          reason: comment.cancelReason || 'Não informado'
        }))
        
        setComments(formattedComments)
      } catch (error) {
        console.error('Erro ao carregar comentários:', error)
        setComments([])
        setLoading(false)
      } finally {
        setLoading(false)
      }
    }

    fetchComments()
  }, [period, merchant?._id])

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress color="primary" />
        <Typography variant="body1" sx={{ marginLeft: 2 }}>
          Carregando comentários...
        </Typography>
      </LoadingContainer>
    )
  }

  if (!comments || comments.length === 0) {
    return (
      <StyledCard>
        <CardContent>
          <EmptyState>
            <Cancel sx={{ fontSize: 48, color: '#ccc', marginBottom: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum comentário de cancelamento encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Não há comentários para o período selecionado.
            </Typography>
          </EmptyState>
        </CardContent>
      </StyledCard>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      
      {/* Inserir total de cancelamentos */}
      <Typography variant="h6" gutterBottom sx={{ marginBottom: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        Total de Cancelamentos: {comments.length}
      </Typography>
      
      <Typography variant="h6" gutterBottom sx={{ marginBottom: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning color="warning" />
        Comentários de Cancelamento
      </Typography>

      {comments.map((comment) => (
        <StyledCard key={comment.id}>
          <CardContent>
            <CommentHeader>
              <Avatar sx={{ bgcolor: '#ff6b6b' }}>
                <Person />
              </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {dateFormatMonth(comment.date)}
                  </Typography>
                  <Typography variant="caption" color="text.primary" sx={{ display: 'block' }}>
                    {comment.reason}
                  </Typography>
                </Box>
            </CommentHeader>
            
            <CommentContent variant="body1">
              {comment.comment}
            </CommentContent>
          </CardContent>
        </StyledCard>
      ))}
    </Box>
  )
}
